import { FC, Fragment, useMemo } from 'react';
import { css } from '@emotion/react';
import { GetCallsResponse } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  callIntelUtils,
  callStatusUtils,
  ContactUserName,
  AdaptiveTimeLabel,
  AiOutcomeColumn,
  checkForPrivateCallsInCallList,
} from '@frontend/call-intel-core';
import { EmptyStateConfig as InfinitePaginatedListEmptyStateConfig, InfinitePaginatedList } from '@frontend/components';
import { PrivateCommsBanner, CallIntelPrivateCommsBannerSource } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Photos } from '@frontend/photos';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  IconButton,
  DocumentFilledIcon,
  DocumentErrorIcon,
  DocumentUndeterminedIcon,
  DocumentWarningIcon,
  EmptyStateConfig,
  ListRow,
  SkeletonLoaders,
  SpinningLoader,
} from '@frontend/design-system';
import { trackingIds } from '../../../tracking-ids';

const getTrayIcon = (call?: CallIntelTypes.CallRecord, isDemoAccount?: boolean, isEdited?: boolean) => {
  if (!call?.status) return;
  const { isProcessingCall, isFailedCall, isSkippedCall, isPoorAudio } = callStatusUtils.getCallFlags(call);
  if (isEdited) return <Icon name='document-sent' color='success' size={16} />;
  if (isProcessingCall) return <SpinningLoader size='small' css={styles.progressBar} />;
  if (isFailedCall) return <DocumentErrorIcon color='error' size={16} />;
  if (isSkippedCall) return <DocumentUndeterminedIcon color='subdued' size={16} />;
  if (!isDemoAccount && isPoorAudio) return <DocumentWarningIcon color='warn' size={16} />;
  return (
    <span>
      <DocumentFilledIcon color='light' size={16} />
    </span>
  );
}; //TODO: Fix it from parent itself

type CallRecordsMobileViewProps = {
  emptyStateConfig: EmptyStateConfig<CallIntelTypes.CallRecord>;
  infiniteQueryProps: UseInfiniteQueryResult<
    {
      rows: GetCallsResponse['newCalls'];
      hasPrivateCalls?: boolean;
    },
    unknown
  >;
  smallModeColumns: Record<string, (call: CallIntelTypes.CallRecord) => React.ReactNode>;
  customToolbarRender?: React.ReactNode;
  searchResultSummary?: React.ReactNode;

  // Data configuration
  dataConfig: {
    editedCallIds: string[];
    isDemoAccount?: boolean;
    privateCommBannerSource: CallIntelPrivateCommsBannerSource;
  };
  handlers: {
    onRowActionClick: (rowData: CallIntelTypes.CallRecord) => void;
  };
  height?: number | string;
};

type CallRecordListItemProps = {
  call: CallIntelTypes.CallRecord;
  smallModeColumns: Record<string, (call: CallIntelTypes.CallRecord) => React.ReactNode>;
  onRowActionClick: (rowData: CallIntelTypes.CallRecord) => void;
  // Data configuration
  dataConfig: {
    editedCallIds: string[];
    isDemoAccount?: boolean;
  };
}; //TODO:Create Base type for common props

export const CallRecordItem = ({ call, smallModeColumns, dataConfig, onRowActionClick }: CallRecordListItemProps) => {
  const { t } = useTranslation('analytics');

  const { isDemoAccount, editedCallIds } = dataConfig;

  const renderedColumns = useMemo(() => {
    return Object.entries(smallModeColumns)
      .map(([columnId, renderFn]) => {
        const rendered = renderFn(call);
        return rendered != null ? <Fragment key={columnId}>{rendered}</Fragment> : null;
      })
      .filter(Boolean);
  }, [call, smallModeColumns]);

  const trayIconButton = useMemo(() => {
    const {
      isFailedCall,
      isSkippedCall,
      isProcessingCall,
      isPoorAudio: statusIsPoorAudio,
    } = callStatusUtils.getCallFlags(call);

    const isPoorAudio = isDemoAccount ? false : statusIsPoorAudio;
    const isEdited = editedCallIds.includes(call.id ?? '');

    return (
      <IconButton
        label={
          isEdited
            ? t('Edits to this Call Insight have been saved.')
            : isProcessingCall
            ? t('We are analyzing this call.')
            : isFailedCall
            ? t('We are unable to transcribe and analyze this call.')
            : isSkippedCall
            ? t(
                'This call was excluded from analysis because it had only one participant or the audio was too short or too long. Please review the transcript.'
              )
            : isPoorAudio
            ? t(
                'The transcript and details of this call may be inaccurate due to poor audio quality. Please review the recording.'
              )
            : t('Call Insights')
        }
        onClick={() => onRowActionClick(call)}
        css={styles.iconButton}
        data-testid='call-insight-action-button'
        trackingId={trackingIds.callIntel.openCallTakeAwayTray}
      >
        {getTrayIcon(call, isDemoAccount, isEdited)}
      </IconButton>
    );
  }, [call, isDemoAccount, onRowActionClick, editedCallIds, t]);

  return (
    <ListRow css={{ padding: theme.spacing(1.5), cursor: 'pointer', gridTemplateColumns: 'auto 1fr' }}>
      <ListRow.Lead css={{ alignItems: 'flex-start' }}>
        <Photos.ContactProfilePhoto
          locationId={call.locationId}
          personId={call.person?.id || ''}
          name={callIntelUtils.getFullName(call.person?.firstName, call.person?.lastName, t('Unknown'))}
          disableClick={!call.person?.id}
          size='small'
        />
      </ListRow.Lead>
      <ListRow.Content>
        <div css={styles.titleRow}>
          <div css={styles.titleName}>
            <ContactUserName
              callDirection={
                call.direction === CallIntelTypes.CallDirectionEnum.DIRECTION_OUTBOUND ? 'outbound' : 'inbound'
              }
              contact={{
                ...callIntelUtils.getContactInfo(call),
                defaultName: t('Unknown'),
              }}
              locationId={call?.locationId ?? ''}
              showAvatar={false}
              highlightName={false}
              showOnlyName
              leftElement={<AiOutcomeColumn.Cell callInsights={call as Call} customStyles={styles.aiOutcomeColumn} />}
              textStyles={{ fontSize: theme.fontSize(14), color: theme.colors.text.default }}
            />
          </div>
          <div css={styles.trayIconWrapper}>
            <AdaptiveTimeLabel startTime={call.startTime} />
            {trayIconButton}
          </div>
        </div>
        {!!renderedColumns.length && (
          <ListRow.Content.Subtitle css={styles.leadContentWrapper} isCompactMode>
            <div css={styles.columnsWrapper}>{renderedColumns}</div>
          </ListRow.Content.Subtitle>
        )}
      </ListRow.Content>
    </ListRow>
  );
};

export const CallRecordsMobileView = ({
  emptyStateConfig,
  infiniteQueryProps,
  smallModeColumns,
  customToolbarRender,
  searchResultSummary,
  handlers,
  dataConfig,
  height,
}: CallRecordsMobileViewProps) => {
  const { onRowActionClick } = handlers;

  const hasPrivateCalls = useMemo(
    () => checkForPrivateCallsInCallList(infiniteQueryProps.data?.pages || []),
    [infiniteQueryProps.data?.pages]
  );

  return (
    <>
      {!!customToolbarRender && (
        <div css={styles.toolbarWrapper}>
          {customToolbarRender}
          {searchResultSummary}
          {hasPrivateCalls && <PrivateCommsBanner source={dataConfig.privateCommBannerSource} />}
        </div>
      )}
      <InfinitePaginatedList
        emptyStateConfig={emptyStateConfig as InfinitePaginatedListEmptyStateConfig}
        endOfListText={''}
        infiniteQueryProps={infiniteQueryProps}
        height={height}
        renderListItem={({ listItem }) => (
          <CallRecordItem
            key={listItem.id}
            call={listItem}
            smallModeColumns={smallModeColumns}
            onRowActionClick={onRowActionClick}
            dataConfig={dataConfig}
          />
        )}
        skeletonLoader={<CallListLoader />}
      />
    </>
  );
};

const CallListLoader: FC = () => {
  return (
    <div css={styles.callItem}>
      <SkeletonLoaders.Loader height={36} />
    </div>
  );
};

const styles = {
  callItem: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing(1)};
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    margin: ${theme.spacing(1)};
  `,
  toolbarWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      padding-top: ${theme.spacing(1)};
    }
  `,
  columnsWrapper: css`
    display: flex;
    gap: ${theme.spacing(1)};
    flex-wrap: wrap;
    align-items: center;
  `,
  leadContentWrapper: css`
    white-space: pre-line;
    font-weight: inherit;
    display: flex;
  `,
  leadTitleContentWrapper: css`
    display: flex;
    font-weight: inherit;
  `,
  titleRow: css`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    column-gap: ${theme.spacing(1)};
  `,
  titleName: css`
    min-width: 0;
  `,
  aiOutcomeColumn: css`
    width: auto;
    > span > div {
      display: flex;
      align-items: center;
    }
  `,
  trayIconWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  progressBar: css`
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
    > div {
      height: ${theme.spacing(2)};
      width: ${theme.spacing(2)};
      margin: ${theme.spacing(0)};
    }
  `,
  iconButton: css`
    padding: ${theme.spacing(1)};
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    min-width: ${theme.spacing(3)};
    min-height: ${theme.spacing(3)};
  `,
};
